/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { useSubscriptionInvoices } from '@features/subscription/hooks/useSubscriptionInvoices';
import StatusBadge from '@/shared/ui/components/basic/StatusBadge';
import type { StatusBadgeTone } from '@/shared/ui';
import { Anchor } from '@/shared/ui/components/basic/Anchor';
import { DataState } from '@/shared/ui/components/basic/data-state/DataState';
import { CardListSkeleton } from '@/shared/ui/components/basic/data-state/skeletons';
import type { InvoiceItem } from '@services/requests/housien-services/AgenciesService.subscription';
import { Text, UnstyledButton } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';

interface Props {
  agencyId: string;
}

const CARD =
  'border border-(--border-subtle) bg-(--surface-secondary) p-(--space-5) rounded-(--radius-lg) ' +
  'transition-[border-color,background-color] duration-[var(--transition-standard)]';

const HISTORY_DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

/**
 * Formats a price given in cents as a whole-real currency string.
 *
 * @param cents - The price in integer cents.
 * @returns The formatted `R$` amount.
 */
function formatPrice(cents: number): string {
  return `R$ ${Math.floor(cents / 100)}`;
}

/**
 * Maps an invoice status to a status-badge tone.
 *
 * @param status - The invoice status.
 * @returns The corresponding badge tone.
 */
function invoiceTone(
  status: string,
): StatusBadgeTone {
  const map: Record<string, StatusBadgeTone> = {
    paid: 'success',
    pending: 'warning',
    overdue: 'error',
    cancelled: 'neutral',
    refunded: 'neutral',
  };
  return map[status] ?? 'neutral';
}

/**
 * Resolves the localized label for an invoice status, falling back to the raw
 * status key.
 *
 * @param status - The invoice status.
 * @param t - The translation function.
 * @returns The localized status label.
 */
function invoiceLabel(status: string, t: (key: string) => string): string {
  return t(`subscription.history.invoiceStatus.${status}`) || status;
}

/**
 * Single invoice row rendering date, amount, status badge, and a download link.
 *
 * @param props - The invoice item and the translation function.
 * @returns The invoice row.
 */
function InvoiceRow({ inv, t }: { inv: InvoiceItem; t: (key: string) => string }) {
  return (
    <div className="flex flex-col gap-(--space-1) border-b border-(--border-ultra-subtle) py-(--space-3) md:grid md:grid-cols-[2fr_1fr_1fr_auto] md:items-center md:gap-(--space-3)">
      <Text component="span" className="md:text-center" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}>
        {formatLocalizedDate(inv.date, undefined, HISTORY_DATE_OPTS)}
      </Text>
      <Text component="span" className="md:text-center"
        style={{
          fontSize: 'var(--text-body-sm)',
          fontWeight: 600,
          color: 'var(--text-primary)',
        }}
      >
        {formatPrice(inv.amount_cents)}
      </Text>
      <div className="flex items-center md:justify-center">
        <StatusBadge tone={invoiceTone(inv.status)}>{invoiceLabel(inv.status, t)}</StatusBadge>
      </div>
      {inv.nfse_url || inv.invoice_url ? (
        <Anchor
          href={(inv.nfse_url || inv.invoice_url)!}
          target="_blank"
          rel="noopener noreferrer"
          underline="never"
          style={{
            fontSize: 'var(--text-meta)',
            fontWeight: 600,
            color: 'var(--seed-base)',
            fontFamily: 'var(--font-family)',
          }}
        >
          {inv.nfse_url ? t('subscription.history.downloadNfe') : t('subscription.history.downloadInvoice')}
        </Anchor>
      ) : (
        <Text component="span"
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--text-tertiary)',
          }}
        >
          —
        </Text>
      )}
    </div>
  );
}

/**
 * Paginated invoice history card. Uses useSubscriptionInvoices hook.
 */
export function SubscriptionHistoryCard({ agencyId }: Props) {
  const { t } = useTranslation('features/pricing');
  const { invoices, total, page, pageSize, setPage, isLoading, isError } =
    useSubscriptionInvoices(agencyId);

  const start = page * pageSize;
  const hasPrev = page > 0;
  const hasNext = start + pageSize < total;

  // Bare: this is the contents of the "see all invoices" dialog now, and a
  // settings block inside a dialog would be a card inside a card.
  return (
    <div data-testid="subscription-history-block">
      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!isLoading && !isError && invoices.length === 0}
        loadingFallback={<CardListSkeleton count={4} />}
        emptyState={
          <div
            style={{
              fontSize: 'var(--text-body-sm)',
              color: 'var(--text-tertiary)',
              padding: 'var(--space-4) 0',
            }}
          >
            {t('subscription.history.noInvoices')}
          </div>
        }
      >
        <div className="flex flex-col">
          <div
            className="hidden grid-cols-[2fr_1fr_1fr_auto] gap-(--space-3) border-b border-(--border-subtle) pb-(--space-2) md:grid"
            style={{
              fontSize: 'var(--text-meta)',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.05em',
            }}
          >
            {[t('subscription.history.columns.date'), t('subscription.history.columns.amount'), t('subscription.history.columns.status'), ''].map((h, i) => (
              <Text component="span" key={h || `col-${i}`} className={i < 3 ? 'text-center' : undefined}>{h}</Text>
            ))}
          </div>
          {invoices.map((inv) => (
            <InvoiceRow key={inv.invoice_id} inv={inv} t={t} />
          ))}
        </div>
        {total > pageSize && (
          <div className="mt-(--space-3) flex flex-wrap items-center justify-center gap-(--space-2)">
            <UnstyledButton
              disabled={!hasPrev}
              onClick={() => setPage(page - 1)}
              className="rounded-(--radius-md) border border-(--border-subtle) px-(--space-3) py-(--space-1) text-[length:var(--text-meta)] font-semibold disabled:opacity-40"
              style={{
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: hasPrev ? 'pointer' : 'default',
                fontFamily: 'var(--font-family)',
              }}
            >
              {t('subscription.history.prevPage')}
            </UnstyledButton>
            <Text component="span"
              data-testid="pagination-range"
              style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)', flexShrink: 0 }}
            >
              {start + 1} a {Math.min(start + pageSize, total)} de {total}
            </Text>
            <UnstyledButton
              disabled={!hasNext}
              onClick={() => setPage(page + 1)}
              className="rounded-(--radius-md) border border-(--border-subtle) px-(--space-3) py-(--space-1) text-[length:var(--text-meta)] font-semibold disabled:opacity-40"
              style={{
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: hasNext ? 'pointer' : 'default',
                fontFamily: 'var(--font-family)',
              }}
            >
              {t('subscription.history.nextPage')}
            </UnstyledButton>
          </div>
        )}
      </DataState>
    </div>
  );
}
